import { useUpdateEffect } from 'ahooks';
import React, { PropsWithChildren, ReactNode, RefObject, useCallback, useLayoutEffect, useRef } from 'react';
import { ScrollArea } from '@ibsys/kit';
import { cn, scrollIntoViewWithinContainer } from '@/shared/lib';

type ChatHistoryProps = {
  autoDownScroll?: boolean;
  /**
   * Значение, смена которого прокручивает историю вниз. Без него вниз уезжает любое изменение
   * содержимого — и догруженная более ранняя история уводила бы просмотр от прочитанного места.
   */
  autoDownScrollKey?: string | number;
  /** Узел перед первым сообщением: маркер и признак подгрузки более ранней истории */
  topSlot?: ReactNode;
  /** Прокручиваемая область истории — наблюдателю маркера подгрузки и восстановлению места просмотра */
  viewportRef?: RefObject<HTMLDivElement | null>;
  className?: string;
  classNames?: {
    content?: string;
  };
};

/**
 * Базовый компонент для отображения истории чата
 * Обрабатывает только скролл и отображение children
 * Логика группировки и обработки сообщений должна быть в местах использования
 */
function ChatNotMemo({
  children,
  autoDownScroll = true,
  autoDownScrollKey,
  topSlot,
  viewportRef,
  className,
  classNames,
}: PropsWithChildren<ChatHistoryProps>) {
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const viewportNodeRef = useRef<HTMLDivElement | null>(null);
  const isOpenedRef = useRef(false);

  const setViewportNode = useCallback(
    (node: HTMLDivElement | null) => {
      viewportNodeRef.current = node;
      if (viewportRef) viewportRef.current = node;
    },
    [viewportRef],
  );

  /**
   * Область прокрутки указывается явно: полосу прокрутки `ScrollArea` включает только следующим
   * рендером, а до него область прокручиваемой не выглядит, и поиск её по стилю не находит ничего —
   * прокрутка открывшейся ленты молча пропадала бы.
   */
  const scrollDown = useCallback(() => {
    scrollIntoViewWithinContainer(messagesEndRef.current, {
      behavior: 'instant',
      container: viewportNodeRef.current,
    });
  }, []);

  // Открытие ленты: до первой отрисовки — иначе на кадр показалось бы её начало
  useLayoutEffect(() => {
    if (isOpenedRef.current || !autoDownScroll) return;

    isOpenedRef.current = true;
    scrollDown();
    // Высота ленты уточняется после первой отрисовки — повтор следующим кадром доводит её до конца
    const rafId = requestAnimationFrame(scrollDown);

    return () => cancelAnimationFrame(rafId);
  }, [autoDownScroll, scrollDown]);

  // Частые обновления (например, во время стриминга) сводятся к одной прокрутке за кадр
  useUpdateEffect(() => {
    if (!autoDownScroll) return;

    const rafId = requestAnimationFrame(scrollDown);

    return () => cancelAnimationFrame(rafId);
  }, [autoDownScrollKey ?? children, autoDownScroll]);

  return (
    <div className={cn('h-full flex flex-col justify-end relative', className)}>
      <ScrollArea aria-orientation="vertical" viewPortProps={{ ref: setViewportNode }}>
        {topSlot}
        <div className={cn('flex h-full flex-col gap-3 relative w-full p-1 pr-3', classNames?.content)}>{children}</div>
        <div ref={messagesEndRef} />
      </ScrollArea>
    </div>
  );
}

export const Chat = React.memo(ChatNotMemo);
